import { Button, DrawerBody, DrawerFooter, DrawerHeader, Heading, Stack, Text } from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import { AircraftType } from '@pelicargo/types';
import { Show, useError, useSuccess } from '@pelicargo/ui';
import { DayOfWeek, InstructionUsage } from '@prisma/client';
import { formatInTimeZone } from 'date-fns-tz';
import { omit } from 'lodash';
import { useCallback, useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { trpc, TrpcInputs } from '../../config/trpc';
import { addressOptional } from '../../hooks/quote/useAddressForm';
import { Location, LocationAutocomplete } from '../Autocomplete/LocationAutocomplete';
import { BaseDrawer } from '../BaseDrawer';
import { InstructionForm } from '../Forms/InstructionForm';
import { SimpleForm } from '../SimpleForm';

const baseSelectors = z.object({
  provider_id: z.string().min(1, 'Required'),
  airport_id: z.string().min(1, 'Required'),
  aircraft_type: z.nativeEnum(AircraftType).nullish(),
  flight_number: z.string().nullish(),
  airline_product: z.string().nullish(),
});

const schema = z.discriminatedUnion('usage', [
  baseSelectors.extend({
    usage: z.literal(InstructionUsage.QUOTING),
    booking_instructions: z.string().nullable(),
    dropoff_instructions: z.string().nullable(),
    delivery_address: addressOptional,
    dropoff_contact_id: z.coerce.number().nullable(),
    from_dock_hours: z.coerce.number().nullable(),
    to_dock_hours: z.coerce.number().nullable(),
  }),
  baseSelectors.extend({
    usage: z.literal(InstructionUsage.BOOKING),
    cutoff_at_hours_before: z.coerce.number().nullish(),
    cutoff_at_time: z.string().time().nullish(),
    cutoff_at_time_days_before: z.coerce.number().nullish(),
    schedule: z.array(z.nativeEnum(DayOfWeek)).nullish(),
  }),
]);

export type InstructionFormValues = z.infer<typeof schema>;

type Props = {
  instructionId?: number | null;
  isOpen: boolean;
  onClose: () => void;
};

export const InstructionFormDrawer = ({ instructionId, isOpen, onClose }: Props) => {
  const { handleSuccess } = useSuccess();
  const { handleError } = useError();
  const methods = useForm<Exclude<TrpcInputs['updateInstruction'], void>>({
    resolver: zodResolver(schema),
  });

  const { data } = trpc.getInstruction.useQuery(
    {
      id: instructionId as number,
    },
    {
      enabled: !!instructionId,
    },
  );
  const { mutateAsync: createInstruction, isLoading: isCreating } = trpc.createInstruction.useMutation();
  const { mutateAsync: updateInstruction, isLoading: isUpdating } = trpc.updateInstruction.useMutation();

  useEffect(() => {
    if (!instructionId) {
      methods.reset();
    }
    if (instructionId) {
      methods.reset({
        ...omit(data, ['id', 'created_at', 'updated_at', 'cutoff_at_time']),
        cutoff_at_time: data?.cutoff_at_time ? formatInTimeZone(data.cutoff_at_time, 'Etc/UTC', 'HH:mm') : undefined,
      });
    }
  }, [data, instructionId, methods]);

  const copy = useMemo(() => {
    return instructionId ? 'Update Instruction' : 'Create Instruction';
  }, [instructionId]);

  const handleLocationSelect = async (data: Location) => {
    methods.setValue('delivery_address.recipient', data?.recipient);
    methods.setValue('delivery_address.line_one', data?.lineOne);
    methods.setValue('delivery_address.city', data?.city);
    methods.setValue('delivery_address.state', data?.state);
    methods.setValue('delivery_address.zip_code', data?.zipCode);
    methods.setValue('delivery_address.country', data?.country);
  };

  const onValid = useCallback(
    async (values: InstructionFormValues) => {
      try {
        if (instructionId) {
          await updateInstruction({
            ...values,
            id: instructionId,
          });
        }

        if (!instructionId) {
          await createInstruction({
            ...values,
          });
        }

        onClose();
        handleSuccess(instructionId ? 'Instruction updated' : 'Instruction created');
      } catch (error) {
        handleError(error);
      }
    },
    [instructionId, onClose, handleSuccess, updateInstruction, createInstruction, handleError],
  );

  const handleSubmit = methods.handleSubmit(onValid);

  const handleClose = () => {
    methods.reset();
    onClose();
  };

  return (
    <BaseDrawer isOpen={isOpen} onClose={handleClose}>
      <DrawerHeader>
        <Heading variant="h4">{copy}</Heading>
      </DrawerHeader>
      <DrawerBody>
        <SimpleForm {...methods} submitOnBlur={false}>
          <Stack spacing="4">
            <LocationAutocomplete onSelect={handleLocationSelect} />
            <InstructionForm />
          </Stack>
        </SimpleForm>
      </DrawerBody>
      <DrawerFooter>
        <Stack direction="row" justify={instructionId ? 'space-between' : 'right'} align="center" w="full" spacing="2">
          <Show if={!!instructionId}>
            <Text>ID: {instructionId}</Text>
          </Show>
          <Button type="submit" colorScheme="brand" isLoading={isCreating || isUpdating} onClick={handleSubmit}>
            {copy}
          </Button>
        </Stack>
      </DrawerFooter>
    </BaseDrawer>
  );
};
